import React, { useEffect, useState } from "react";
import { fetchSamples, createSample, updateSample, deleteSample, uploadSamplesCsv, ValuationSamplePayload, ValuationSample } from "../services/valuationService";
import { listDossiers, getDossier, DossierSummary } from "../services/dossierService";

const AdminValuationPage: React.FC = () => {
  const [samples, setSamples] = useState<ValuationSample[]>([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState<ValuationSamplePayload>({
    marque: "",
    modele: "",
    annee: "",
    kilometrage: "",
    energie: "",
    boite: "",
    finition: "",
    prix: "",
    source_url: "",
    pays: "",
    date_releve: "",
    admin_validated: true,
  });
  const [busy, setBusy] = useState(false);
  const [dossiers, setDossiers] = useState<DossierSummary[]>([]);
  const [dossierIndex, setDossierIndex] = useState(0);
  const [selectedDossierId, setSelectedDossierId] = useState<number | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [csvInfo, setCsvInfo] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const data = await fetchSamples();
      setSamples(data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    const loadDossiers = async () => {
      try {
        const data = await listDossiers();
        setDossiers(data);
        if (data.length) {
          setSelectedDossierId(data[0].id);
          setDossierIndex(0);
        }
      } catch (e) {
        console.error("Erreur chargement dossiers pour préremplir", e);
      }
    };
    loadDossiers();
  }, []);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      if (editingId) {
        await updateSample(editingId, form);
        setEditingId(null);
      } else {
        await createSample(form);
      }
      setForm({ ...form, prix: "" });
      load();
    } catch (e) {
      console.error(e);
      setError("Impossible d'enregistrer l'échantillon");
    } finally {
      setBusy(false);
    }
  };

  const handleCsvUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || !e.target.files[0]) return;
    setUploading(true);
    setCsvInfo(null);
    setError(null);
    try {
      const res = await uploadSamplesCsv(e.target.files[0]);
      setCsvInfo(`Import terminé: ${res?.created || 0} échantillons ajoutés.`);
      await load();
    } catch (err: any) {
      setError(err?.message || "Import CSV échoué");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const onEdit = (sample: ValuationSample) => {
    setEditingId(sample.id);
    setForm({
      marque: sample.marque || "",
      modele: sample.modele || "",
      annee: sample.annee || "",
      kilometrage: sample.kilometrage ? String(sample.kilometrage) : "",
      energie: sample.energie || "",
      boite: sample.boite || "",
      finition: sample.finition || "",
      prix: sample.prix ? String(sample.prix) : "",
      source_url: sample.source_url || "",
      pays: sample.pays || "FR",
      date_releve: sample.date_releve || new Date().toISOString().slice(0, 10),
      admin_validated: sample.admin_validated ?? true,
      door_count: sample.door_count ? String(sample.door_count) : "",
      seat_count: sample.seat_count ? String(sample.seat_count) : "",
    });
  };

  const onDelete = async (id: number) => {
    setBusy(true);
    setError(null);
    try {
      await deleteSample(id);
      setSamples((prev) => prev.filter((s) => s.id !== id));
      if (editingId === id) {
        setEditingId(null);
        setForm({ ...form, prix: "" });
      }
    } catch (e) {
      console.error(e);
      setError("Suppression impossible");
    } finally {
      setBusy(false);
    }
  };

  const prefillFromDossier = async (dossierId: number) => {
    if (!dossiers.length) return;
    const dossier = dossiers.find((d) => d.id === dossierId) || dossiers[0];
    try {
      const detail = await getDossier(dossier.id);
      const veh = (detail as any).cerfa_state?.vehicle || {};
      setForm((prev) => ({
        ...prev,
        marque: veh.brand || "",
        modele: veh.model || "",
        annee: veh.firstRegistrationDate || "",
        kilometrage: veh.mileage || "",
        energie: veh.energy || veh.carburant || "",
        boite: veh.boite || "",
        finition: veh.type || "",
        source_url: "",
        pays: "FR",
        date_releve: new Date().toISOString().slice(0, 10),
        admin_validated: true,
      }));
    } catch (e) {
      console.error("Erreur préremplissage dossier", e);
    }
  };

  const prefillNext = () => {
    if (!dossiers.length) return;
    const next = (dossierIndex + 1) % dossiers.length;
    setDossierIndex(next);
    setSelectedDossierId(dossiers[next].id);
    prefillFromDossier(dossiers[next].id);
  };

  const input = (label: string, field: keyof ValuationSamplePayload, placeholder = "") => (
    <div className="flex flex-col">
      <label className="text-[11px] text-slate-300 mb-1">{label}</label>
      <input
        className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
        value={(form[field] as string) || ""}
        onChange={(e) => setForm({ ...form, [field]: e.target.value })}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="p-4 md:p-6 text-white">
      <div className="flex justify-between items-center mb-4 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold">Échantillons de cote (admin)</h1>
          <p className="text-xs text-slate-400">Préremplis à partir des dossiers / Cerfa pour gagner du temps.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {dossiers.length > 0 && (
            <div className="flex items-center gap-2">
              <select
                className="bg-slate-900 border border-slate-700 rounded px-2 py-1 text-sm"
                value={selectedDossierId ?? ""}
                onChange={(e) => {
                  const val = Number(e.target.value);
                  setSelectedDossierId(val);
                  const idx = dossiers.findIndex((d) => d.id === val);
                  if (idx >= 0) setDossierIndex(idx);
                }}
              >
                {dossiers.map((d) => (
                  <option key={d.id} value={d.id}>
                    #{d.id} {d.immatriculation || d.titre || ""}
                  </option>
                ))}
              </select>
              <button
                onClick={() => selectedDossierId && prefillFromDossier(selectedDossierId)}
                className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm"
              >
                Préremplir dossier #{selectedDossierId}
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm cursor-pointer">
              Import CSV
              <input
                type="file"
                accept=".csv,text/csv"
                onChange={handleCsvUpload}
                className="hidden"
              />
            </label>
            {uploading && <span className="text-xs text-slate-400">Import en cours...</span>}
            {csvInfo && <span className="text-xs text-emerald-300">{csvInfo}</span>}
          </div>
        </div>
      </div>
      {error && <div className="text-sm text-red-300 mb-2">{error}</div>}
      <div className="grid md:grid-cols-3 gap-3 bg-slate-900/70 border border-slate-800 p-3 rounded-lg">
        {input("Marque", "marque")}
        {input("Modèle", "modele")}
        {input("Année (AAAA ou AAAA-MM)", "annee", "2020-05")}
        {input("Kilométrage", "kilometrage", "85000")}
        {input("Énergie", "energie", "essence/diesel/electrique")}
        {input("Boîte", "boite", "auto/manuelle")}
      {input("Finition/variante", "finition")}
      {input("Prix (€)", "prix")}
      {input("Source (URL)", "source_url", "https://...")}
      {input("Pays/Région", "pays", "FR")}
      {input("Date relevé", "date_releve", "2025-12-01")}
      {input("Portes", "door_count", "4")}
      {input("Places", "seat_count", "5")}
      </div>
      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <button
          onClick={submit}
          disabled={busy}
          className="px-3 py-2 bg-emerald-600 hover:bg-emerald-500 rounded-md text-sm font-semibold disabled:opacity-60"
        >
          {busy ? "Enregistrement..." : editingId ? "Mettre à jour l'échantillon" : "Ajouter l'échantillon"}
        </button>
        <label className="px-3 py-2 bg-slate-800 border border-slate-700 rounded text-sm cursor-pointer">
          Import CSV
          <input
            type="file"
            accept=".csv,text/csv"
            onChange={handleCsvUpload}
            className="hidden"
          />
        </label>
        {uploading && <span className="text-xs text-slate-400">Import en cours...</span>}
        {csvInfo && <span className="text-xs text-emerald-300">{csvInfo}</span>}
        {dossiers.length > 1 && (
          <button
            onClick={prefillNext}
            className="px-3 py-2 bg-slate-700 hover:bg-slate-600 rounded-md text-sm"
          >
            Préremplir suivant
          </button>
        )}
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-semibold mb-2">Échantillons récents</h2>
        {loading && <div className="text-sm text-slate-400">Chargement...</div>}
        {!loading && samples.length === 0 && <div className="text-sm text-slate-500">Aucun échantillon pour le moment.</div>}
        {!loading && samples.length > 0 && (
          <div className="overflow-auto max-h-[60vh] border border-slate-800 rounded">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-800">
                <tr>
                  <th className="px-2 py-1 text-left">Date</th>
                  <th className="px-2 py-1 text-left">Vehicule</th>
                  <th className="px-2 py-1 text-left">Km</th>
                  <th className="px-2 py-1 text-left">Énergie</th>
                  <th className="px-2 py-1 text-left">Boîte</th>
                  <th className="px-2 py-1 text-left">Finition</th>
                  <th className="px-2 py-1 text-left">Portes</th>
                  <th className="px-2 py-1 text-left">Places</th>
                  <th className="px-2 py-1 text-left">Prix</th>
                  <th className="px-2 py-1 text-left">Source</th>
                  <th className="px-2 py-1 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {samples.map((s) => (
                  <tr key={s.id} className="border-b border-slate-800">
                    <td className="px-2 py-1 text-slate-300">{s.date_releve || s.created_at?.slice(0, 10)}</td>
                    <td className="px-2 py-1">
                      <div className="text-slate-100 font-semibold">{s.marque} {s.modele}</div>
                      <div className="text-[11px] text-slate-400">{s.annee} • {s.energie} • {s.boite}</div>
                    </td>
                    <td className="px-2 py-1 text-slate-300">{s.kilometrage}</td>
                    <td className="px-2 py-1 text-slate-300">{s.energie || "-"}</td>
                    <td className="px-2 py-1 text-slate-300">{s.boite || "-"}</td>
                    <td className="px-2 py-1 text-slate-300">{s.finition || "-"}</td>
                    <td className="px-2 py-1 text-slate-300">{s.door_count ?? "-"}</td>
                    <td className="px-2 py-1 text-slate-300">{s.seat_count ?? "-"}</td>
                    <td className="px-2 py-1 text-slate-100">{s.prix} €</td>
                    <td className="px-2 py-1 text-[11px] text-slate-400 truncate max-w-[160px]">
                      {s.source_url || "-"}
                    </td>
                    <td className="px-2 py-1 text-[11px] space-x-2">
                      <button
                        onClick={() => onEdit(s)}
                        className="px-2 py-1 bg-slate-700 hover:bg-slate-600 rounded text-xs"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => s.id && onDelete(s.id)}
                        className="px-2 py-1 bg-red-700 hover:bg-red-600 rounded text-xs"
                      >
                        Supprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminValuationPage;
